import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { cn } from "@/lib/utils";
import { MessageSquare } from "lucide-react";

export function ChatNotificationBell() {
  const { profile } = useCurrentUser();
  const userId = profile?.id;

  const { data: summary = [] } = useQuery({
    queryKey: ["chat-unread-summary", userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("unread_message_summary")
        .select("unread_count")
        .eq("user_id", userId);

      if (error) {
        console.error("[ChatNotificationBell] query failed:", error);
        return [];
      }
      return data ?? [];
    },
    enabled: !!userId,
    staleTime: 30_000,
  });

  const totalUnread = useMemo(
    () => summary.reduce((sum, row) => sum + (row.unread_count ?? 0), 0),
    [summary],
  );

  return (
    <button
      className="relative p-1.5 rounded-md hover:bg-muted transition-colors"
      aria-label="Chat messages"
      title={`${totalUnread} unread message${totalUnread !== 1 ? "s" : ""}`}
    >
      <MessageSquare className={cn("h-4.5 w-4.5", totalUnread > 0 ? "text-foreground" : "text-muted-foreground")} />
      {totalUnread > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center leading-none">
          {totalUnread > 99 ? "99+" : totalUnread}
        </span>
      )}
    </button>
  );
}
